import React, { FC, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Spinner from 'vibo-ui/Spinner';
import Questions from 'components/questions/Questions';
import QuestionsBlockHeader from './QuestionsBlockHeader';
import NoQuestions from 'components/emptyStates/NoQuestions';
import SectionBlock from 'components/sections/blocks/SectionBlock';

import { EventContext } from 'components/context/EventContext';
import { TimelineQuestionsContext } from './TimelineQuestionsContext';
import { AllTimelineGqlOptions, useTimeline } from '../TimelineContext';

import { useTargetingQuestion } from 'graphql/hooks/questions';
import { getQuestionsData } from 'services/questions/helpers';

import { ControlProps } from 'components/sections/blocks/SectionBlock/interfaces';
import { SectionBlockBase } from 'components/sections/blocks/SectionBlocks/interfaces';
import { EventTimelineTabKey } from 'types/enums';

import useStyles from './style';

const TimelineQuestionsBlock: FC<SectionBlockBase> = ({ sections, section }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [controlRef, setControlRef] = useState<Nullable<ControlProps>>(null);

  const { isFavoriteSections, isEventDj, isLoading, canEdit } = useContext(EventContext);
  const {
    variables,
    questions: { queries },
  } = useTimeline<AllTimelineGqlOptions>();

  const { sectionId } = useParams<keyof EventPageRouteParams>();

  const { targetQuestionId } = useTargetingQuestion();

  const { data, loading: questionsLoading } = useQuery<QuestionsListResponse>(
    queries.questions.query,
    {
      variables: {
        ...variables,
        sectionId,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      skip: !sectionId,
    }
  );

  const questionsData: QuestionsData = getQuestionsData(data);

  const questions = (isFavoriteSections ? questionsData : questionsData.questions) as Question[];

  useEffect(() => {
    if (!!targetQuestionId) {
      controlRef?.setIsCollapsed(false);
    }
  }, [targetQuestionId]);

  return (
    <TimelineQuestionsContext.Provider
      value={{
        section,
        sections,
        questions,
        questionsData,
      }}
    >
      <SectionBlock
        controlRef={setControlRef}
        className={classNames('content-fade-in', classes.timelineQuestionsBlock, {
          blankEmptyState: questionsLoading,
          noSpaces: isEventDj && canEdit && (section.questionsCount > 0 || questions.length > 0),
          'ready _2': (!!questionsData && !questionsLoading) || !isLoading,
        })}
        type={EventTimelineTabKey.questions}
        section={section}
        renderHeader={({ caretButton }) => (
          <QuestionsBlockHeader>{caretButton}</QuestionsBlockHeader>
        )}
        renderContent={() => (
          <>
            {!!questions.length ? (
              <Questions onAddQuestion={() => controlRef?.setIsShown(true)} />
            ) : questionsLoading ? (
              <Spinner />
            ) : (
              <NoQuestions>{t('addYourFirstQuestion')}</NoQuestions>
            )}
          </>
        )}
        defaultShown
        canCollapse
      />
    </TimelineQuestionsContext.Provider>
  );
};

export default TimelineQuestionsBlock;
